import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import { localePath } from "./i18n/config";

import LocalizedRoute from "./i18n/LocalizedRoute";

import AddUserForm from "./components/AddUserForm";
import AlertForm from "./components/AlertForm";
import CouponForm from "./components/CouponForm";
import ITSupportEditForm from "./components/ITSupportEditForm";
import ITSupportForm from "./components/ITSupportForm";
import NurseEditForm from "./components/NurseEditForm";
import NurseForm from "./components/NurseForm";
import RegisterCustomerViaEmailForm from "./components/RegisterCustomerViaEmailForm";
import RegisterItSupport from "./components/RegisterItSupport";
import RegisterNurseForm from "./components/RegisterNurseForm";
import RegisterSeniorForm from "./components/senior-reg-form/RegisterSeniorForm";
import SeniorHomeForm from "./components/SeniorHomeForm";
import ServiceForm from "./components/ServiceForm";
import UserEditForm from "./components/UserEditForm";
import { clearUserError } from "./redux/actions";
import AboutUs from "./screens/AboutUs";
import AlertsList from "./screens/AlertsList";
import Article from "./screens/Article";
import BlogsList from "./screens/BlogsList";
import Cart from "./screens/Cart";
import Contact from "./screens/Contact";
import ContactInfo from "./screens/ContactInfo";
import CouponsList from "./screens/CouponsList";
import EndSubscription from "./screens/EndSubscription";
import FAQPage from "./screens/FAQ";
import ForCompanies from "./screens/ForCompanies";
import Health from "./screens/Health";
import Home from "./screens/home/Home";
import Instructions from "./screens/Instructions";
import ITSupport from "./screens/ITSupport";
import Login from "./screens/Login";
import MemoryGame from "./screens/memoryGame/MemoryGame";
import Nurse from "./screens/Nurse";
import NurseSeniors from "./screens/NurseSeniors";
import Orders from "./screens/Orders";
import Osta from "./screens/osta/Osta";
import PaymentMethod from "./screens/PaymentMethod";
import Phone from "./screens/phone/Phone";
import PlaceOrder from "./screens/PlaceOrder";
import PrivacyPolicy from "./screens/PrivacyPolicy";
import ProductsScreen from "./screens/products/Products";
import RecoverPassword from "./screens/RecoverPassword";
import Register from "./screens/Register";
import SeniorHomesList from "./screens/SeniorHomesList";
import Services from "./screens/Services";
import ServicesList from "./screens/ServicesList";
import Shipping from "./screens/Shipping";
import TermsOfUse from "./screens/TermsOfUse";
import Thanks from "./screens/thanks/Thanks";
import UpdatePassword from "./screens/UpdatePassword";
import UserPage from "./screens/UserPage";
import Users from "./screens/Users";
import Video from "./screens/Video";
import VideoCallApp from "./screens/videoCallApp/videoCallApp";
import VideoCallRoom from "./screens/VideoCallRoom";
import Watch from "./screens/watch/Watch";

/** Renders public routes under /:locale(en|sv) prefix */
const LocalizedRouteWrapper = () => {
    const { path } = useRouteMatch();
    return (
        <Switch>
            <Route path={path} exact component={Home} />
            {/* Two-segment: product categories (buy/senior-tablet, kop/seniorsurfplatta) */}
            <Route path={`${path}/buy/:cat`} exact component={ProductsScreen} />
            <Route path={`${path}/kop/:cat`} exact component={ProductsScreen} />
            {/* Two-segment: blog articles */}
            <Route path={`${path}/happysenior-blog/:id`} exact component={Article} />
            <Route path={`${path}/happysenior-blogg/:id`} exact component={Article} />
            <Route path={`${path}/blog/:id`} exact component={Article} />
            {/* Two-segment: account tabs */}
            <Route path={`${path}/account/:tab_id`} exact component={UserPage} />
            <Route path={`${path}/konto/:tab_id`} exact component={UserPage} />
            {/* Two-segment: password reset */}
            <Route path={`${path}/password/reset/:userId/:token`} exact component={UpdatePassword} />
            <Route path={`${path}/password/reset`} exact component={RecoverPassword} />
            <Route path={`${path}/losenord/aterstall`} exact component={RecoverPassword} />
            {/* Single-slug catch-all */}
            <Route path={`${path}/:slug`} exact component={LocalizedRoute} />
        </Switch>
    );
};


const Routes = () => {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(clearUserError());
        return () => {};
    }, [dispatch]);

    return (
        <main
            style={{
                minHeight: `calc(100vh - 226px)`,
            }}
        >
            <Switch>
                <Route path="/" component={Home} exact />
                <Route path="/osta" component={Osta} exact />
                <Route path="/senioritabletti" component={Services} exact />
                <Route path="/senioripuhelin" component={Phone} exact />
                <Route path="/turvaranneke" component={Watch} exact />
                <Route path="/videopuhelu_appi" component={VideoCallApp} exact />
                <Route path="/thanks" component={Thanks} exact />
                <Route path="/osta/:cat" component={ProductsScreen} exact />
                <Route path="/copyright" component={PrivacyPolicy} exact />
                <Route path="/palveluehdot" component={TermsOfUse} exact />
                <Route path="/kirjaudu" component={Login} />
                <Route path="/tili/:tab_id" component={UserPage} />
                <Route path="/subscription_end" component={EndSubscription} exact />
                <Route path="/user" render={() => <Redirect to={localePath("tili", "tilaus")} />} />
                <Route path="/tili" render={() => <Redirect to={localePath("tili", "tilaus")} />} />
                <Route path="/ostoskori" component={Cart} />
                <Route path="/toimitus" component={Shipping} />
                <Route path="/maksutapa" component={PaymentMethod} />
                <Route path="/tee_tilaus" component={PlaceOrder} />
                <Route path="/yhteydenotto" component={Contact} />
                <Route path="/contact_info" component={ContactInfo} />
                <Route path="/yrityksille" component={ForCompanies} />
                <Route path="/usein_kysyttya" component={FAQPage} />
                <Route path="/admin/users/add" component={AddUserForm} />
                <Route path="/invite/staff/customer" component={RegisterCustomerViaEmailForm} />
                <Route path="/admin/users/:id" component={UserEditForm} />
                <Route path="/admin/users" component={Users} />
                <Route path="/admin/orders" component={Orders} />
                <Route path="/admin/service/:id" component={ServiceForm} />
                <Route path="/admin/services" component={ServicesList} />
                <Route path="/admin/alert/:id" component={AlertForm} />
                <Route path="/admin/alerts" component={AlertsList} />
                <Route path="/admin/coupon/:id" component={CouponForm} />
                <Route path="/admin/coupons" component={CouponsList} />
                <Route path="/admin/blogs" component={BlogsList} />
                <Route path="/luo_tili" component={Register} />
                <Route path="/salasana/vaihda" component={RecoverPassword} />
                <Route path="/happysenior_blogi" component={Health} />
                <Route path="/instructions" component={Instructions} />
                <Route path="/blog/:id" component={Article} />
                <Route path="/video" component={Video} />
                <Route path="/password/reset/:userId/:token" component={UpdatePassword} exact />
                <Route path="/password/reset" component={RecoverPassword} exact />
                <Route path="/aboutus" component={AboutUs} />
                <Route path="/admin/senior_home/:id" component={SeniorHomeForm} />
                <Route path="/admin/senior_homes" component={SeniorHomesList} />
                <Route path="/admin/nurse" exact component={Nurse} />
                <Route path="/admin/nurses/add" exact component={NurseForm} />
                <Route path="/admin/nurses/:id" exact component={NurseEditForm} />
                <Route path="/admin/nurses/seniors/:id" exact component={NurseSeniors} />
                <Route path="/invite/staff/nurse" component={RegisterNurseForm} />
                <Route path="/invite/staff/head_nurse" component={RegisterNurseForm} />

                <Route path="/admin/it_support" exact component={ITSupport} />
                <Route path="/admin/it_support/add" exact component={ITSupportForm} />
                <Route path="/invite/staff/it_support" component={RegisterItSupport} />
                <Route path="/admin/it_support/:id" exact component={ITSupportEditForm} />
                <Route path="/meet/:token/:id" exact component={VideoCallRoom} />

                <Route path="/services" render={() => <Redirect to={localePath("palvelu")} />} />
                <Route path="/subscribe" render={() => <Redirect to={localePath("osta")} />} />
                <Route path="/shoppingcart" render={() => <Redirect to={localePath("ostoskori")} />} />
                <Route path="/shipping" render={() => <Redirect to={localePath("toimitus")} />} />
                <Route path="/paymentmethod" render={() => <Redirect to={localePath("maksutapa")} />} />
                <Route path="/placeorder" render={() => <Redirect to={localePath("tee_tilaus")} />} />
                <Route path="/terms" render={() => <Redirect to={localePath("palveluehdot")} />} />
                <Route path="/login" render={() => <Redirect to={localePath("kirjaudu")} />} />
                <Route path="/contact" render={() => <Redirect to={localePath("yhteydenotto")} />} />
                <Route path="/companies" render={() => <Redirect to={localePath("yrityksille")} />} />
                <Route path="/register" render={() => <Redirect to={localePath("luo_tili")} />} />
                <Route path="/password/recover" render={() => <Redirect to={localePath("salasana/vaihda")} />} />
                <Route path="/health" render={() => <Redirect to={localePath("happysenior_blogi")} />} />

                {/* add senior route */}
                <Route path="/admin/senior/add" component={RegisterSeniorForm} />
                {/* muistipeli / memory game  */}
                <Route path="/muistipeli" component={MemoryGame} />

                {/* Flat translated routes (no locale prefix) — for local dev and fallback */}
                <Route path="/buy/:cat" component={ProductsScreen} exact />
                <Route path="/kop/:cat" component={ProductsScreen} exact />
                <Route path="/happysenior-blog/:id" component={Article} exact />
                <Route path="/happysenior-blogg/:id" component={Article} exact />
                <Route path="/account/:tab_id" component={UserPage} exact />
                <Route path="/konto/:tab_id" component={UserPage} exact />
                <Route path="/losenord/aterstall" component={RecoverPassword} exact />

                {/* Locale-prefixed routes (en/sv) with translated slugs */}
                <Route path="/:locale(en|sv)" component={LocalizedRouteWrapper} />

                {/* Single-slug catch-all for translated routes (no prefix) */}
                <Route path="/:slug" component={LocalizedRoute} exact />
            </Switch>
        </main>
    );
};

export default Routes;
